import { FunctionComponent, useState } from "react";
import i18n from "../../i18n/index.js";
import { getNewsletterFieldSettings } from "../../selectors/modules.js";
import { getActiveSite } from "../../selectors/sites.js";
import { useAppSelector } from "../../store/hooks.js";
import moduleBaseStyles from "../../stylesheets/components/module-base.module.css";
import styles from "../../stylesheets/components/newsletter-module.module.css";
import {
  BaseModuleProps,
  FormSubmitState,
  NewsletterModuleSettings,
} from "../../types/index.js";
import {
  getActiveColorScheme,
  getTranslations,
  scrollToElSingle,
} from "../../utils/utils.js";
import ModuleHeadings from "../ModuleHeadings.js";
import ModuleWithHeadings from "../ModuleWithHeadings.js";
import NewsletterForm from "../NewsletterForm.js";
import RichEditorWrapper from "../RichEditorWrapper.js";

type Props = BaseModuleProps<NewsletterModuleSettings>;

const NewsletterModule: FunctionComponent<Props> = ({
  translatedModule,
  translatedModule: {
    id,
    siteId,
    settings: { textAlign, layout },
    translation: {
      languageId,
      settings: { title, subtitle },
    },
  },
  isFirstOnPage,
  isPreview,
  pageId,
  queries,
}) => {
  const scheme = useAppSelector((state) =>
    getActiveColorScheme(
      state.colorSchemes,
      getActiveSite(state.sites),
      translatedModule,
    ),
  );
  const modules = useAppSelector((state) => state.modules);
  const fieldSettings = getNewsletterFieldSettings(modules, languageId);

  const [submitState, setSubmitState] = useState<FormSubmitState>("initial");

  const trans = getTranslations(languageId, i18n);
  const formId = `newsletter-${id}`;

  const showFieldBackgrounds = layout === "layout-2" || layout === "layout-4";
  const isWideLayout = layout === "layout-3" || layout === "layout-4";

  return (
    <ModuleWithHeadings
      title={title}
      subtitle={subtitle}
      id={id}
      className={styles.NewsletterModule}
      colors={{ background: scheme.main.background, color: scheme.main.text }}
    >
      <div className={moduleBaseStyles.Module__Wrapper}>
        <ModuleHeadings
          scheme={scheme}
          isFirstOnPage={isFirstOnPage}
          textAlign={textAlign}
          title={title}
          subtitle={subtitle}
        />

        <RichEditorWrapper
          module={translatedModule}
          pageId={pageId}
          languageId={languageId}
          isPreview={isPreview}
          scheme={scheme}
          className={`TextAlign--${textAlign.description}`}
        />
      </div>
      <div className={moduleBaseStyles.Module__Wrapper}>
        {submitState !== "submitted" && (
          <NewsletterForm
            formId={formId}
            languageId={languageId}
            siteId={siteId}
            trans={trans}
            submitState={submitState}
            setSubmitState={setSubmitState}
            isPreview={isPreview}
            fieldSettings={fieldSettings}
            scheme={scheme}
            showFieldBackgrounds={showFieldBackgrounds}
            isWideLayout={isWideLayout}
            queries={queries}
          />
        )}
        {submitState === "submitted" && (
          <p
            ref={(el) => {
              el && scrollToElSingle(el);
            }}
            className={styles.NewsletterModule__Message}
          >
            {trans.newsletterMessages.success}
          </p>
        )}
        {submitState === "error" && (
          <p className={styles.NewsletterModule__Message}>
            {trans.newsletterMessages.error}
          </p>
        )}
      </div>
    </ModuleWithHeadings>
  );
};

export default NewsletterModule;
